import { Plus } from "lucide-react";
import { AddOrEditMatterModal } from "@/components/features/matter/layout/AddOrEditMatterModal/AddOrEditMatterModal";
import { MassTortMattersSearchBar } from "@/components/features/mass-torts/MassTortMattersSearchBar";
import { MassTortMattersTable } from "@/components/features/mass-torts/MassTortMattersTable";
import { Button } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { type MassTortMatterSearchStatusCategory } from "@/lib/data/requests";
import { ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

interface MassTortMattersListViewProps {
  massTortId: string;
}

/**
 * Mass tort “matters” list: loads rows via mass tort matter search.
 */
export function MassTortMattersListView({
  massTortId,
}: MassTortMattersListViewProps) {
  const { hasActionPermission } = useUser();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [category, setMatterStatusCategory] =
    useState<MassTortMatterSearchStatusCategory>("pre-litigation");

  const canCreate = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.create,
  );

  return (
    <>
      <div id="mass-tort-matters-list" className="flex min-h-0 flex-1 flex-col">
        <MassTortMattersTable
          massTortId={massTortId}
          category={category}
          searchBar={
            <MassTortMattersSearchBar
              category={category}
              onMatterStatusCategoryChange={setMatterStatusCategory}
            />
          }
          tableName={tanstackTableNames.massTortMatters}
          onRowClick={async (row) => {
            await navigate({ to: `/matters/${row.original.id}` });
          }}
          tableButtons={
            canCreate && (
              <Button
                variant="foreground"
                size="md"
                className="shrink-0"
                onClick={() => {
                  setIsCreating(true);
                }}
                data-testid="mass-tort-create-matter-button"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Create matter
              </Button>
            )
          }
        />
      </div>

      <AddOrEditMatterModal
        open={isCreating}
        setOpen={setIsCreating}
        editing={false}
        creatingMore={false}
        allowedMatterStatusCategories={[
          ...ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS,
        ]}
        massTortId={massTortId}
        afterCreateFn={(res) => navigate({ to: `/matters/${res.data.id}` })}
      />
    </>
  );
}
